import { Box, Button, Icon, Text } from '@everlywell/ui-kit';
import { X, MagnifyingGlass } from '@phosphor-icons/react';
import React, { useCallback, useState } from 'react';
import { trackEva } from 'utils/eva';

import { useEvaChat } from '../hooks/useEvaChat';
import { useModalShell } from '../hooks/useModalShell';
import { useRenderGuard } from '../hooks/useRenderGuard';
import type { EvaChatModalProps } from '../types/components';
import ChatMessageInput from './ChatMessageInput';
import ChatMessagesContainer from './ChatMessagesContainer';
import EvaWelcomeScreen from './EvaWelcomeScreen';

type ChatModalShellProps = {
  onClose: () => void;
  hasConversationStarted: boolean;
  children: React.ReactNode;
  onSwitchToSearch?: () => void;
};

function ChatModalShell({
  onClose,
  hasConversationStarted,
  children,
  onSwitchToSearch,
}: ChatModalShellProps): JSX.Element {
  useModalShell({ onClose });

  return (
    <Box
      position="fixed"
      top="0"
      left="0"
      right="0"
      bottom="0"
      zIndex={2000}
      height="calc(100vh-32px)"
      width="100vw"
      background={hasConversationStarted ? '#F9F9F6' : 'eggplant.wash'}
      overflow="hidden"
      display="flex"
      alignItems="center"
      justifyContent="center"
      role="dialog"
      aria-modal="true"
      aria-label="Eva"
      sx={{
        transition: 'background-color 0.5s ease-in-out',
      }}
    >
      <Box
        position="fixed"
        top="0 !important"
        zIndex={1}
        backgroundColor="eggplant.wash"
        height="32px"
        width="100%"
      />
      {onSwitchToSearch && (
        <Button
          type="button"
          onClick={onSwitchToSearch}
          variant="ghost"
          display="flex"
          position="absolute"
          top={hasConversationStarted ? '0.55rem' : '1.25rem'}
          left={hasConversationStarted ? 'auto' : 'auto'}
          right={hasConversationStarted ? '3.5rem' : '3.75rem'}
          height="2rem"
          paddingX="12px"
          alignItems="center"
          gap="6px"
          borderRadius="1.26875rem"
          backgroundColor="tints.white"
          color="eggplant.base"
          zIndex={2001}
          _hover={{ filter: 'brightness(0.97)' }}
          _active={{ filter: 'brightness(0.94)' }}
          fontFamily="DM Sans"
          fontSize="sm"
          fontWeight="medium"
        >
          <Icon
            as={MagnifyingGlass}
            aria-hidden="true"
            color="eggplant.base"
            boxSize={14}
          />
          <Text as="span" display={['none', 'inline']}>
            Back to Search
          </Text>
        </Button>
      )}
      {!hasConversationStarted && (
        <Button
          type="button"
          onClick={onClose}
          variant="ghost"
          position="absolute"
          top="1.25rem"
          right="1.25rem"
          width="2rem"
          height="2rem"
          minWidth="2rem"
          minHeight="2rem"
          padding="0.5rem"
          borderRadius="1.26875rem"
          backgroundColor="tints.white"
          color="eggplant.base"
          zIndex={2001}
          _hover={{ filter: 'brightness(0.97)' }}
          _active={{ filter: 'brightness(0.94)' }}
          aria-label="Close Eva"
        >
          <Icon
            as={X}
            aria-hidden="true"
            color="eggplant.base"
            boxSize={16}
          />
        </Button>
      )}
      {children}
    </Box>
  );
}

export default function EvaChatModal({
  onClose,
  isOpen = true,
  onSwitchToSearch,
  initialMessage,
  location,
}: EvaChatModalProps): JSX.Element | null {
  const shouldRender = useRenderGuard(isOpen);
  const [isTypingComplete, setIsTypingComplete] = useState(true);
  const initialMessageSentRef = React.useRef(false);

  const {
    sendMessage,
    inputValue,
    handleInputChange,
    messages,
    isThinking,
    isStreaming,
    activeAgentMessageId,
    activeStreamEventSeq,
    isLoading,
    recommendations,
    error,
    getConversationId,
  } = useEvaChat();

  const hasSentFirstMessage = messages.length > 0;
  
  const handleTypingComplete = useCallback((isComplete: boolean) => {
    setIsTypingComplete(isComplete);
  }, []);

  const handleSend = useCallback(
    async (value: string) => {
      const trimmed = value.trim();
      if (!trimmed) return;
      const didSend = await sendMessage(trimmed);
      if (didSend) {
        // Analytics: Tracks successful EVA search queries for funnel analysis.
        // Side effect: User input is logged as part of the event payload; keep
        // this aligned with privacy / PII guidelines when changing properties.
        trackEva('Sent Eva Search Message', {
          user_message: trimmed,
          conversation_id: getConversationId(),
          experience: 'conversation',
          ...(location ? { location } : {}),
        });
      }
    },
    [getConversationId, sendMessage],
  );

  React.useEffect(() => {
    if (
      initialMessage &&
      initialMessage.trim() &&
      !initialMessageSentRef.current &&
      shouldRender
    ) {
      initialMessageSentRef.current = true;
      void handleSend(initialMessage);
    }
  }, [initialMessage, shouldRender, handleSend]);

  if (!shouldRender) {
    return null;
  }

  return (
    <ChatModalShell
      onClose={onClose}
      hasConversationStarted={hasSentFirstMessage}
      onSwitchToSearch={onSwitchToSearch}
    >
      {hasSentFirstMessage ? (
        <>
          <ChatMessagesContainer
            messages={messages}
            isThinking={isThinking}
            isStreaming={isStreaming}
            activeAgentMessageId={activeAgentMessageId}
            activeStreamEventSeq={activeStreamEventSeq}
            recommendations={recommendations}
            errorMessage={error}
            onClose={onClose}
            onSendMessage={sendMessage}
            onTypingComplete={handleTypingComplete}
          />
          <Box
            position="fixed"
            bottom={['24px', 0]}
            left="50%"
            transform="translateX(-50%)"
            width="calc(100% - 32px)"
            maxWidth={800}
            display="flex"
            justifyContent="center"
            pointerEvents="auto"
          >
            <ChatMessageInput
              value={inputValue}
              onChange={handleInputChange}
              onSubmit={(v) => void handleSend(v)}
              isAnimationDisabled={messages.length > 0}
              hasConversationStarted={messages.length > 0}
              isDisabled={isStreaming || isThinking || isLoading || !isTypingComplete}
              maxWidth={800}
            />
          </Box>
        </>
      ) : (
        <EvaWelcomeScreen
          value={inputValue}
          onChange={handleInputChange}
          onSubmit={handleSend}
          isDisabled={isLoading}
        />
      )}
    </ChatModalShell>
  );
}
